// Sign-in with Zalo, Google and Apple, on the website and inside the iOS app. Every provider ends in a
// POST to /api/auth/:provider, where the server checks it with the provider before signing anyone in.
//   Website: Google's own button (Google Identity Services) and Apple's popup give an ID token; Zalo is a
//   full-page redirect through /auth/zalo.
//   App: Google and Apple use the phone's native sheets (@capgo/capacitor-social-login); Zalo opens the
//   phone's browser and comes back through macromate://auth/zalo?code=… (Google doesn't allow sign-in
//   inside an app's web view).

const scripts = {};
const loadScript = (src) => {
  scripts[src] ||= new Promise((resolve, reject) => {
    const tag = document.createElement('script');
    tag.src = src;
    tag.async = true;
    tag.onload = resolve;
    tag.onerror = () => {
      delete scripts[src];
      reject(new Error('Không tải được nút đăng nhập, kiểm tra mạng rồi thử lại'));
    };
    document.head.appendChild(tag);
  });
  return scripts[src];
};

export class SignInCancelled extends Error {}

export async function fetchProviders(apiUrl = '') {
  const response = await fetch(`${apiUrl}/api/auth/providers`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Có lỗi xảy ra (${response.status})`);
  return response.json();
}

// Sends what the provider returned to the server. `token` (the app's) is only set when connecting a provider
// to an account signed in on the app; the website uses its session cookie and CSRF token.
export async function postSignIn({ apiUrl = '', provider, body, intent = 'sign_in', token = null, native = false }) {
  const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
  const response = await fetch(`${apiUrl}/api/auth/${provider}`, {
    method: 'POST',
    credentials: native ? 'omit' : 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(!native && csrf ? { 'X-CSRF-Token': csrf } : {}),
    },
    body: JSON.stringify({ ...body, intent, ...(native && intent === 'sign_in' ? { device: 'iOS' } : {}) }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || `Có lỗi xảy ra (${response.status})`);
  return data;
}

// ---------- Website ----------

// Renders Google's button into `element`; `onToken` gets the ID token after the user picks an account.
export async function renderGoogleButton(element, clientId, onToken) {
  await loadScript('https://accounts.google.com/gsi/client');
  window.google.accounts.id.initialize({ client_id: clientId, callback: ({ credential }) => onToken(credential), ux_mode: 'popup' });
  window.google.accounts.id.renderButton(element, {
    theme: 'outline', size: 'large', shape: 'pill', text: 'continue_with', locale: 'vi', width: Math.min(element.offsetWidth || 320, 400),
  });
}

export async function appleWebSignIn(serviceId) {
  await loadScript('https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/vi_VN/appleid.auth.js');
  window.AppleID.auth.init({ clientId: serviceId, scope: 'name email', redirectURI: window.location.origin, usePopup: true });
  try {
    const { authorization, user } = await window.AppleID.auth.signIn();
    return { id_token: authorization.id_token, name: [user?.name?.lastName, user?.name?.firstName].filter(Boolean).join(' ') };
  } catch (e) {
    if (e?.error === 'popup_closed_by_user' || e?.error === 'user_cancelled_authorize') throw new SignInCancelled();
    throw new Error('Không đăng nhập được bằng Apple, thử lại nhé');
  }
}

// ---------- App ----------

// Resolves to { SocialLogin } once the plugin is initialised. Never resolve a promise with the plugin itself:
// a Capacitor plugin answers every property, `then` included, so a promise takes it for another promise and
// waits on it forever (the Google button spun endlessly).
let nativeReady = null;
const nativePlugin = (providers) => {
  nativeReady ||= import('@capgo/capacitor-social-login').then(async ({ SocialLogin }) => {
    await SocialLogin.initialize({
      ...(providers.google ? { google: { iOSClientId: providers.google.ios_client_id, iOSServerClientId: providers.google.web_client_id } } : {}),
      ...(providers.apple ? { apple: {} } : {}),
    });
    return { SocialLogin };
  }).catch((e) => {
    nativeReady = null;
    throw e;
  });
  return nativeReady;
};

const cancelled = (e) => /cancel/i.test(`${e?.message || ''} ${e?.code || ''}`);

export async function nativeGoogle(providers) {
  const { SocialLogin } = await nativePlugin(providers);
  try {
    // forcePrompt: without it the plugin silently reuses the last Google account on this phone (even after
    // signing out of Macro Mate), so no one could pick another account.
    const { result } = await SocialLogin.login({ provider: 'google', options: { scopes: ['email', 'profile'], forcePrompt: true } });
    if (!result.idToken) throw new Error('Google không trả về thông tin đăng nhập');
    return { id_token: result.idToken };
  } catch (e) {
    if (cancelled(e)) throw new SignInCancelled();
    throw e;
  }
}

export async function nativeApple(providers) {
  const { SocialLogin } = await nativePlugin(providers);
  try {
    const { result } = await SocialLogin.login({ provider: 'apple', options: { scopes: ['email', 'name'] } });
    if (!result.idToken) throw new Error('Apple không trả về thông tin đăng nhập');
    // Apple gives the name only the first time; the server keeps it then.
    return { id_token: result.idToken, name: [result.profile?.familyName, result.profile?.givenName].filter(Boolean).join(' ') };
  } catch (e) {
    if (cancelled(e)) throw new SignInCancelled();
    throw e;
  }
}

// Opens Zalo in the phone's browser and resolves with the one-time code the server sends back to the app.
export async function nativeZalo(siteUrl) {
  const [{ App }, { Browser }] = await Promise.all([import('@capacitor/app'), import('@capacitor/browser')]);
  return new Promise((resolve, reject) => {
    let done = false;
    const handles = [];
    const finish = (fn) => {
      if (done) return;
      done = true;
      handles.forEach((h) => h.remove());
      fn();
    };
    Promise.all([
      App.addListener('appUrlOpen', ({ url }) => {
        if (!url.startsWith('macromate://auth/zalo')) return;
        const params = new URL(url.replace('macromate://', 'https://app/')).searchParams;
        Browser.close().catch(() => {});
        finish(() => (params.get('code') ? resolve(params.get('code')) : reject(new Error(params.get('error') || 'Không đăng nhập được bằng Zalo'))));
      }),
      Browser.addListener('browserFinished', () => setTimeout(() => finish(() => reject(new SignInCancelled())), 500)),
    ]).then((added) => {
      handles.push(...added);
      return Browser.open({ url: `${siteUrl}/auth/zalo?app=1`, presentationStyle: 'popover' });
    }).catch((e) => finish(() => reject(e)));
  });
}
